import React, { useEffect, forwardRef, useMemo, createRef } from 'react'
import { useDispatch } from 'react-redux'


import ImagesCarousel from '../components/ImagesCarousel'
import InfoPanelContent from '../components/InfoPanelContent'
import MetaData from '../components/MetaData'
import Readmore from '../components/Readmore'
import FavoriteControl from '../components/FavoritesControl'
import { IconList, Icon } from '../components/Icons'

import InfoPanelQuadhashSummary from './InfoPanelQuadhashSummary'
import InfoPanelQuadhashActions from './InfoPanelQuadhashActions'

import BookingIcon from '../icons/bookingcom.svg'
import RatingIcon from '../icons/star.svg'

import { settings } from '../conf'
import { useAccommodation, useGuide } from '../api'

import { range } from '../utils'
import { serializeAvailability } from '../location'

import { setPermentMapPin } from '../slices/map'
import { showSidebar } from '../slices/ui'
import { useAccommodationAvailability } from '../slices/accommodation'

import {
  ACCOMMODATION_DESCRIPTION,
  ADDRESS,
  AMENITIES,
  BOOK_A_ROOM,
  CHANGE_DATE,
  CHECK_AVAILABILITY,
  CONTACTS,
  COST,
  GUEST,
  GUESTS,
  INFO,
  NIGTH,
  NIGTHS,
  NO_ROOMS_AVAILABLE,
  READ_REVIEWS_ON_BOOKING,
  USER_RATING,
  VIEW_ON_BOOKINGCOM,
} from '../trans.js';

const AccommodationSummary = (props) => {
  const { accommodation, route_id, trail_id } = props
  const { elevation, quadhash } = accommodation || {}
  const badge = accommodation?.current_price && (
    <div className="pwa__badge positive big">
      { accommodation.current_price }
    </div>
  )
  return (
  <>
    {badge}
    <InfoPanelQuadhashSummary 
        quadhash={quadhash} 
        elevation={elevation} 
        route_id={route_id} 
        trail_id={trail_id} />
  </>)
}

const AccommodationMetaData = (props) => {
  const accommodation = props.accommodation
  const guide = useGuide()
  if (!accommodation || !guide) return null
  const title = `${accommodation.place_name} | ${accommodation.title} ${accommodation.subtitle}`
  const shareImageTitle = `${accommodation.title} ${accommodation.subtitle} | ${accommodation.place_name}`
  const description = ACCOMMODATION_DESCRIPTION({
    "accommodation_type": accommodation.subtitle,
    "accommodation_name": accommodation.title,
    "place_title": accommodation.place_name,
    "route_title": guide.title,
  })
  var jsonLd = {
    "@context": "https://schema.org",
    "@type": "Hotel",
    "name": accommodation.title,
    "address": accommodation.address
  }
  if (accommodation.description){
    jsonLd['description'] = accommodation.description
  }
  if (accommodation.image){
    jsonLd['photo'] = accommodation.image.medium
  }
  if (accommodation.cost){
    jsonLd['priceRange'] = range(1, accommodation.cost_range).map((x) => '$').join('')
  }
  if (accommodation.review || accommodation.rating){
    jsonLd = {
      "@context": "https://schema.org/",
      "@type": "Review",
      "itemReviewed": {...jsonLd},
      "author": {
        "@type": "Person", 
        "name": "Giusy Forwalk", 
        "url": "https://www.facebook.com/giusy.forwalk.org"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Forwalk.org",
        "url": "https://forwalk.org",
        "logo": settings.STATIC_URL + 'images/logo_publisher.svg'
      }
    }
    if (accommodation.review) {
      jsonLd['reviewBody'] = accommodation.review
    }
    if (accommodation.rating) {
      jsonLd["reviewRating"] = {
        "@type": "Rating",
        "ratingValue": accommodation.rating,
        "bestRating": "5",
        "worstRating": "1"
      }
    }
  }

  return <MetaData title={title} 
                   description={description} 
                   shareImageTitle={title}
                   jsonLd={jsonLd} />

}

const AccommodationInfo = (props) => {
  const { id, route_id, trail_id } = props
  const dispatch = useDispatch()
  const availability = useAccommodationAvailability()
  const accommodation = useAccommodation({ 
    id: id, 
    availability: availability ? serializeAvailability(availability) : null 
  })
  const { title, subtitle, images, description, review, amenities, uuid, lnglat, current_price,
    quadhash, services, contacts, booking_url, affiliate_url, cost, address, rating } = accommodation || {}
  let baseActions = []
  const feature = {
    id: id,
    type: 'accommodation',
    label: title,
    lnglat: lnglat
  }

  const availabilityText = (current_price) ? [
    (availability.guest > 1) ? `${availability.guest} ${GUESTS}`: "", 
    (availability.guest > 1 && availability.nigth > 1) ? '/' : "",
    (availability.nigth > 1) ? `${availability.nigth} ${NIGTHS}`: ""].join("") : null

  if (booking_url){
    baseActions.push({
      onClick: () => window.open(booking_url),
      text: `${BOOK_A_ROOM} ${current_price} ${availabilityText}`,
      className: 'positive',
      pinned: true,
    })
  }

  if (affiliate_url){
    baseActions.push({
      onClick: () => dispatch(showSidebar('reservation')),
      text: availability ? booking_url ? CHANGE_DATE : NO_ROOMS_AVAILABLE : CHECK_AVAILABILITY,
      className: !availability ? 'primary' : '',
      pinned: !availability || (availability && !booking_url)
    })
    baseActions.push({
      onClick: () => window.open(affiliate_url + "&activeTab=htReviews#tab-reviews"),
      text: READ_REVIEWS_ON_BOOKING,
      className: ''
    })
  } 

  const summary = <AccommodationSummary 
                                  accommodation={accommodation} 
                                  route_id={route_id} 
                                  trail_id={trail_id} />

  const actions = accommodation && <InfoPanelQuadhashActions 
                                      quadhash={quadhash} 
                                      route_id={route_id} 
                                      trail_id={trail_id}  
                                      isStopPoint={true} 
                                      actions={baseActions} />

  useEffect(() => {
    if (lnglat) {
      dispatch(setPermentMapPin(lnglat))
    }
    return () => {
      dispatch(setPermentMapPin(null))
    }
  }, [lnglat, dispatch])

  return (
    <InfoPanelContent key={`accommodation-${id}`}
                      title={title}
                      subtitle={subtitle}
                      images={images}
                      summary={summary}>

      <AccommodationMetaData accommodation={accommodation} />

      { accommodation && (
        <>
        { (description || review ) && (
          <div className="pwa__infopanel__descriptions fade-in delay">
              { description && (
                <p>
                  { description }
                </p>
              )}
              { review && (
                <p>
                  { review }
                </p>
              )}
          </div>
        )}

        <section className="fade-in delay">
            <h3 className="pwa__infopanel__section__title">{INFO}</h3>
            <div className="pwa__infopanel__section__item">
              <span className="title">{ADDRESS}:</span> {address}
            </div>
            { cost && !current_price && (
            <div className="pwa__infopanel__section__item">
                <span className="title">{COST}:</span> {cost}
            </div>
            )}
            { current_price && (
            <div className="pwa__infopanel__section__item">
              <span className="title">{COST}:</span> <strong>{current_price}</strong>  ({availabilityText})
            </div>
            )}

            { rating && (
            <div className="pwa__infopanel__section__item">
                <span className="title">{USER_RATING }:</span> 
                <abbr title={USER_RATING} className="pwa__rating">
                  { range(1, rating).map((x) => <RatingIcon key={x}/>) }
                </abbr>
            </div>
            )}
        </section>

        { services?.length > 0 && (
          <section className='fade-in delay'>
            <h3 className="pwa__infopanel__section__title">{AMENITIES}</h3>
            <IconList icons={services} text={true} className="pwa__infopanel__section__item pwa__infopanel__services__table" />
          </section>
        )}

        { (contacts?.length > 0 || affiliate_url) && (
          <section className='fade-in delay'>
            <h3 className="pwa__infopanel__section__title">{CONTACTS}</h3>
            <ul className="pwa__infopanel__contacts__table">
            { affiliate_url && (
              <li className="button basic">
                <a href={affiliate_url}>
                  <BookingIcon/>
                  <div className="text">{VIEW_ON_BOOKINGCOM}</div>
                </a>
              </li>
            )}
            { contacts.map((contact, key)=>{
                const icon = <Icon icon={contact.type} />
                const text = contact.value
                var link, target
                switch (contact.type) {
                  case 'phone':
                    link = 'tel:' + contact.value
                    break
                  case 'email':
                    link = 'mailto:' + contact.value
                    break
                  default:
                    target = "_blank"
                    link = contact.value
                    break
                }
                return (
                  <li key={key} className="button basic">
                    <a href={link} target={target || '_self'} rel="noopener">
                      {icon} 
                      <div className="text">{text}</div>
                    </a>
                  </li>
                )
            })}
            </ul>
          </section>
        )}

        {actions}

        <div className="pwa__infopanel__action">
            <FavoriteControl feature={feature} className="button raised" />
        </div>

    </>)}

    </InfoPanelContent>
  )
}

export default AccommodationInfo
